/* eslint-disable no-unused-vars */
/* eslint-disable indent */
import React, { useState, useEffect, useRef } from "react";
import { Card, Row, Col, message, Button, Input } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import moment from "moment";
import {
    DropDownComponent,
    TableComponent,
    CalendarComponent,
    RefreshTimestamp
} from "../../CommonComponents";
import "../styles.scss";
import Heading from "../../../../components/Heading";
import { getHubList, getHubCPList } from "../../../../services/api/hub";
import { ReportsService } from "../../../../services/api/reports";
import { downloadOutGoingLCRReport } from "../../../../utils/downloadUtils";
import { useNavigate } from "react-router-dom";
import API from "../../../../api";
import urls from "../../../../api/urls";
import { showErrorMessage } from "../../../../utils";

const OutgoingLCRReport = () => {
    const FILTER_STORAGE_KEY = "OUTGOING_LCR_REPORT_FILTERS";

    const [loading, setLoading] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [dateRange, setDateRange] = useState([
        moment().subtract(1, "days"),
        moment()
    ]);
    const [hubList, setHubList] = useState([]);
    const [selectedHubs, setSelectedHubs] = useState([]);
    const [cpList, setCpList] = useState([]);
    const [selectedDestinationHub, setSelectedDestinationHub] = useState([]);
    const [statusList] = useState([
        { value: "LCR-INSCAN", label: "LCR Inscan" },
        { value: "LCR-OUTSCAN", label: "LCR Outscan" },
        { value: "COMPLETED", label: "Completed" },
        { value: "BOOKED", label: "Booked" },
        { value: "HUB-IN-SCANNED", label: "Hub In-Scanned" },
        { value: "HUB-OUTSCAN", label: "Hub Outscan" },
        // Add more LCR-specific statuses as needed
    ]);
    const [selectedStatus, setSelectedStatus] = useState([]);
    const [stopLcrNums, setStopLcrNums] = useState("");
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
    });
    const [reportData, setReportData] = useState([]);
    const [headerHubList, setHeaderHubList] = useState([]);
    const [hubUser, setHubUser] = useState();
    const [lastsynced, setLastsynced] = useState(null);
    const [nextScheduledRefresh, setNextScheduledRefresh] = useState(null);
    const [activeHubId, setActiveHubId] = useState(JSON.parse(localStorage.getItem("active_hub") || "{}")?.hubId || "ALL_HUBS");

    // Fetch hubs from header/localStorage
    useEffect(() => {
        try {
            const userFullData = JSON.parse(localStorage.getItem("userId") || "{}");
            const userHubs = userFullData?.userInfo?.hubDetails || [];
            if (userHubs?.length > 0) {
                const headerHubs = [
                    { label: "All", value: "ALL" },
                    ...userHubs.map(hub => ({
                        label: hub.hubName,
                        value: hub.hubId
                    }))
                ];
                setHeaderHubList(headerHubs);
            } else {
                // If no hubs assigned, show all available hubs but without "All" option
                setHeaderHubList([]);
            }
        } catch (e) {
            console.error("Error parsing user hubs for Outgoing LCR Report", e);
            setHeaderHubList([]);
        }
    }, []);

    const handleMultiSelectChange = (values, setter, options = []) => {
        if (values.includes("ALL")) {
            const isLatestAll = values[values.length - 1] === "ALL";
            if (isLatestAll) {
                // Select all actual values (excluding the "ALL" option itself)
                const allActualValues = options
                    .filter(opt => opt.value !== "ALL")
                    .map(opt => opt.value);
                setter(allActualValues);
            } else {
                // If it wasn't the latest click, just filter it out (though shouldn't happen with this logic)
                setter(values.filter(v => v !== "ALL"));
            }
        } else {
            setter(values);
        }
    };

    // Ref to track whether we already set the hub for the current login session
    const hubInitializedRef = useRef(false);

    // Helper function to save filters to sessionStorage
    const saveFiltersToStorage = () => {
        const filters = {
            dateRange: dateRange ? [
                dateRange[0]?.format("YYYY-MM-DD"),
                dateRange[1]?.format("YYYY-MM-DD")
            ] : null,
            selectedHubs,
            selectedDestinationHub,
            selectedStatus,
            stopLcrNums,
            pagination: {
                current: pagination.current,
                pageSize: pagination.pageSize
            }
        };
        sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
    };

    // Helper function to restore filters from sessionStorage
    const restoreFiltersFromStorage = () => {
        try {
            const savedFilters = sessionStorage.getItem(FILTER_STORAGE_KEY);
            if (savedFilters) {
                const filters = JSON.parse(savedFilters);
                if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
                    setDateRange([
                        moment(filters.dateRange[0]),
                        moment(filters.dateRange[1])
                    ]);
                }
                if (filters.selectedHubs && filters.selectedHubs.length > 0) {
                    setSelectedHubs(filters.selectedHubs);
                }
                if (filters.selectedDestinationHub && filters.selectedDestinationHub.length > 0) {
                    setSelectedDestinationHub(filters.selectedDestinationHub);
                }
                if (filters.selectedStatus && filters.selectedStatus.length > 0) {
                    setSelectedStatus(filters.selectedStatus);
                }
                if (filters.stopLcrNums) {
                    setStopLcrNums(filters.stopLcrNums);
                }
                if (filters.pagination) {
                    setPagination(prev => ({
                        ...prev,
                        current: filters.pagination.current || prev.current,
                        pageSize: filters.pagination.pageSize || prev.pageSize
                    }));
                }
                // Clear the saved filters after restoring
                sessionStorage.removeItem(FILTER_STORAGE_KEY);
                return true;
            }
        } catch (error) {
            console.error("Error restoring filters:", error);
        }
        return false;
    };

    // Fetch hub list on mount
    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                const hubs = await getHubList();
                setHubList(hubs);
                // Restore filters after hub list is loaded
                restoreFiltersFromStorage();
            } catch {
                message.error("Failed to fetch hub list");
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        const handleHubChanged = () => {
            const activeHub = JSON.parse(localStorage.getItem("active_hub") || "{}");
            const newActiveHubId = activeHub?.hubId;
            setActiveHubId(newActiveHubId || "ALL_HUBS");
            if (newActiveHubId && newActiveHubId !== "ALL_HUBS") {
                setSelectedHubs([newActiveHubId]);
            } else {
                setSelectedHubs(["ALL"]);
            }
        };

        window.addEventListener("hubChanged", handleHubChanged);
        return () => window.removeEventListener("hubChanged", handleHubChanged);
    }, []);

    useEffect(() => {
        const user = JSON.parse(localStorage.getItem("userinfo") || "{}");
        // const userid = JSON.parse(localStorage.getItem("userId") || "{}");
        const role = user?.roles?.[0];
        setHubUser(role);

        const activeHub = JSON.parse(localStorage.getItem("active_hub") || "{}");
        const currentActiveHubId = activeHub?.hubId || "ALL_HUBS";

        if (!hubInitializedRef.current) {
            const userFullData = JSON.parse(localStorage.getItem("userId") || "{}");
            const userHubs = userFullData?.userInfo?.hubDetails || [];

            if (currentActiveHubId && currentActiveHubId !== "ALL_HUBS") {
                setSelectedHubs([currentActiveHubId]);
            } else if (userHubs?.length === 0 || role === "ROLE_HOADMIN" || role === "ROLE_CENTRALOPERATOR") {
                setSelectedHubs([]);
            } else {
                setSelectedHubs(["ALL"]);
            }
            hubInitializedRef.current = true;
        }
    }, [headerHubList]); // Depend on headerHubList to ensure we have options available

    const calendarRef = useRef();

    // Custom handler for date range change
    const handleDateRangeChange = (value) => {
        if (!value || !value[0] || !value[1]) {
            // If cleared, reset to today and open calendar
            setDateRange([moment(), moment()]);
            // Open the calendar dropdown
            if (calendarRef.current && calendarRef.current.picker) {
                calendarRef.current.picker.open();
            }
        } else {
            setDateRange(value);
        }
    };

    // Update useEffect to guard against invalid dateRange
    useEffect(() => {
        if (!hubUser) return;
        if ((hubUser === "ROLE_HUBADMIN" || hubUser === "ROLE_HUBOPERATOR") && selectedHubs.length === 0) return;
        const hasValidDates = Array.isArray(dateRange) && dateRange[0] && dateRange[1] && typeof dateRange[0].format === "function" && typeof dateRange[1].format === "function";
        if (!hasValidDates) return;
        fetchReport();
        // eslint-disable-next-line
    }, [hubUser, JSON.stringify(selectedHubs), JSON.stringify(selectedDestinationHub), JSON.stringify(selectedStatus), stopLcrNums, dateRange, pagination.current, pagination.pageSize]);

    const fetchReport = async () => {
        setLoading(true);
        try {
            const hasValidDates = Array.isArray(dateRange) && dateRange[0] && dateRange[1] && typeof dateRange[0].format === "function" && typeof dateRange[1].format === "function";
            if (!hasValidDates) {
                setLoading(false);
                return;
            }
            setReportData([]); // This is fine here, because the guard above prevents it from running on clear

            // Resolve From Hubs for API logic
            const isSpecialRole = hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR";
            const activeHubOptions = isSpecialRole ? hubList : (headerHubList?.length > 0 ? headerHubList : hubList);
            const resolvedFromHubs = selectedHubs?.includes("ALL")
                ? activeHubOptions.filter(opt => opt.value !== "ALL").map(opt => opt.value)
                : selectedHubs;

            const selectedDestHubData = hubList.filter(hub => hub.value !== "ALL" && selectedDestinationHub?.includes(hub.value));

            const params = {
                ...(resolvedFromHubs?.length > 0 && { originHubId: resolvedFromHubs.join(",") }),
                ...(selectedDestHubData?.length > 0 && { destinationHubId: selectedDestHubData.map(hub => hub.value).join(",") }),
                ...(resolvedFromHubs?.length > 0 && { hubIds: resolvedFromHubs }),
                ...(hasValidDates && {
                    fromDate: dateRange[0].format("YYYY-MM-DD"),
                    toDate: dateRange[1].format("YYYY-MM-DD")
                }),
                ...(selectedStatus?.length > 0 && { currentStatus: selectedStatus.join(",") }),
                ...(stopLcrNums && { stopLcrNums: stopLcrNums }),
                page: (pagination.current - 1), // Convert to 0-based
                size: pagination.pageSize
            };

            // console.log("Incoming LCR Report params:", params);

            // Call the Incoming LCR report API

            const { data, total, lastRefreshedAt, nextScheduledRefresh } = await ReportsService.getOutComingLCRReport(params);
            setReportData(data);
            setLastsynced(lastRefreshedAt);
            setNextScheduledRefresh(nextScheduledRefresh);
            setPagination(prev => ({
                ...prev,
                total,
            }));

        } catch {
            showErrorMessage("Failed to fetch report data");
            setReportData([]);
            setPagination(prev => ({
                ...prev,
                total: 0
            }));
        } finally {
            setLoading(false);
        }
    };

    const handleTableChange = (newPagination) => {
        setPagination(prev => ({
            ...prev,
            current: newPagination.current,
            pageSize: newPagination.pageSize
        }));
    };

    const handleDownload = async () => {
        const hasValidDates = Array.isArray(dateRange) && dateRange[0] && dateRange[1] && typeof dateRange[0].format === "function" && typeof dateRange[1].format === "function";
        if (!hasValidDates) {
            showErrorMessage("Please select a date range for download");
            return;
        }
        setDownloadLoading(true);
        try {
            // Resolve From Hubs for Download logic
            const isSpecialRole = hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR";
            const activeHubOptions = isSpecialRole ? hubList : (headerHubList?.length > 0 ? headerHubList : hubList);
            const resolvedFromHubs = selectedHubs?.includes("ALL")
                ? activeHubOptions.filter(opt => opt.value !== "ALL").map(opt => opt.value)
                : selectedHubs;

            const selectedDestHubData = hubList.filter(hub => hub.value !== "ALL" && selectedDestinationHub?.includes(hub.value));

            const downloadParams = {
                ...(resolvedFromHubs?.length > 0 && { originHubId: resolvedFromHubs.join(",") }),
                ...(selectedDestHubData?.length > 0 && { destinationHubId: selectedDestHubData.map(hub => hub.value).join(",") }),
                ...(resolvedFromHubs?.length > 0 && { hubIds: resolvedFromHubs }),
                ...(hasValidDates && {
                    fromDate: dateRange[0].format("YYYY-MM-DD"),
                    toDate: dateRange[1].format("YYYY-MM-DD")
                }),
                ...(selectedStatus?.length > 0 && { currentStatus: selectedStatus.join(",") }),
                ...(stopLcrNums && { stopLcrNums: stopLcrNums })
            };


            console.log("downloadParams (Outgoing LCRfrom click):", resolvedFromHubs.join(","));




            await downloadOutGoingLCRReport(downloadParams);
            message.success("Report downloaded successfully");
        } catch (error) {
            console.error("Download error:", error);
            message.error("Failed to download report");
        } finally {
            setDownloadLoading(false);
        }
    };

    const navigate = useNavigate();

    // Handler for cell clicks (fetch details and navigate)
    const handleCellClick = async (type, record) => {
        // Save current filters before navigating
        saveFiltersToStorage();

        let status = type;
        // Map shipment types to required API status
        if (type === "shipmentOut") status = "SHIPMENT_OUT";
        else if (type === "shipmentIn") status = "SHIPMENT_IN";
        else if (type === "shipmentPending") status = "SHIPMENT_PENDING";
        // Map in-bag shipment types to regular shipment statuses
        else if (type === "inBagShipmentOut") status = "SHIPMENT_OUT";
        else if (type === "inBagShipmentIn") status = "SHIPMENT_IN";
        else if (type === "inBagShipmentVariance") status = "SHIPMENT_PENDING";
        // Map bag types to required API status
        else if (type === "bagOut") status = "BAG_OUT";
        else if (type === "bagIn") status = "BAG_IN";
        else if (type === "bagPending") status = "BAG_PENDING";

        // Determine detail type and endpoint
        const isInBagShipment = ["inBagShipmentOut", "inBagShipmentIn", "inBagShipmentVariance"].includes(type);
        const isBag = ["bagOut", "bagIn", "bagPending"].includes(type);
        let detailType = isBag ? "Bag" : "Shipment";
        let endpoint = isBag ? urls.BAG_DETAIL_VIEW_LCR : (isInBagShipment ? urls.IN_BAG_SHIPMENT_DETAIL_VIEW_LCR : urls.SHIPMENT_DETAIL_VIEW_LCR);
        let lcrNum = record.stopLcrNum;
        let vehicleNum = record.vehicleNum || record.vehicle_num;
        let currentHub = record.fromHub || record.current_hub;
        try {
            const queryParams = {
                lcrNum,
                type: status,
            };
            const apiResult = await API.Api(endpoint).getBagDetailsParams(queryParams);
            let dataArr = apiResult.response.data.data;
            if (apiResult.response.data.code == 200) {
                if (apiResult.response.data.data.length > 0) {
                    navigate("/detailed-inout-view", {
                        state: {
                            vehicle_lcr_id: lcrNum,
                            vehicle_num: vehicleNum,
                            current_hub: currentHub,
                            type: detailType,
                            status,
                            data: dataArr,
                            pageFrom: "OutGoingLCReport",
                            url: "/outgoing-lcr"
                        },
                    });
                } else {
                    showErrorMessage(apiResult.response.data.message);
                }
            } else {
                showErrorMessage("Something went wrong");
            }





        } catch (error) {
            message.error("An error occurred while fetching details");
        }
    };

    return (
        <div className="report-container">
            <div style={{ backgroundColor: "white", padding: "8px 16px", borderRadius: "8px" }}>
                <Heading title="Outgoing LCR" navigateTo="/reports" newReportUrl="/reports/outgoing-lcr/">
                    <div style={{ paddingTop: "5px" }}>
                        <RefreshTimestamp
                            lastRefreshedAt={lastsynced}
                            nextScheduledRefresh={nextScheduledRefresh}
                        />
                    </div>
                </Heading>
            </div>
            <Card className="report-card">
                <Row gutter={[16, 16]} className="filters-section">
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">From & To Date</div>
                        <CalendarComponent
                            ref={calendarRef}
                            className="custom-calendar"
                            type="range"
                            value={dateRange}
                            onChange={handleDateRangeChange}
                            format="YYYY-MM-DD"
                            allowClear
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">From Hub</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder="From Hub"
                            options={(hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR") ? hubList.filter(o => o.value !== "ALL") : (headerHubList?.length > 0 ? headerHubList : hubList)}
                            onChange={(val) => {
                                const baseOptions = (hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR") ? hubList.filter(o => o.value !== "ALL") : (headerHubList?.length > 0 ? headerHubList : hubList);
                                handleMultiSelectChange(val, setSelectedHubs, baseOptions);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={selectedHubs}
                            allowClear
                            mode="multiple"
                            maxTagCount={1}
                            maxTagPlaceholder={(omittedValues) => `... +${omittedValues.length}`}
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">To Hub</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder="Destination Hub"
                            options={hubList}
                            onChange={(value) => {
                                setSelectedDestinationHub(value);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={selectedDestinationHub}
                            allowClear
                            mode="multiple"
                            maxTagCount="responsive"
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Stop LCR Numbers</div>
                        <Input
                            placeholder="Enter comma separated LCR numbers"
                            value={stopLcrNums}
                            onChange={(e) => setStopLcrNums(e.target.value)}
                            allowClear
                        />
                    </Col>
                    <Col xs={24} sm={24} md={24} lg={24} style={{ display: "flex", alignItems: "flex-end", justifyContent: "flex-end" }}>
                        <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            loading={downloadLoading}
                            onClick={handleDownload}
                        >
                            Download Report
                        </Button>
                    </Col>
                    {/* <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Status</div>
                        <DropDownComponent
                            placeholder="Select Status"
                            options={statusList}
                            onChange={(value) => {
                                setSelectedStatus(value);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={selectedStatus}
                            allowClear
                            mode="multiple"
                            maxTagCount="responsive"
                        />
                    </Col>*/}
                </Row>
                <Row style={{ marginTop: 16 }}>

                </Row>
            </Card>
            <TableComponent
                columns={[
                    { title: "Date", dataIndex: "createdDate", key: "createdDate", sorter: true },
                    {
                        title: "Vehicle LCR Number", dataIndex: "vehicleLcrNum", key: "vehicleLcrNum", sorter: true, align: "center",
                        render: (text, record) => (
                            <span
                                style={{ color: "#2977ff", cursor: "pointer", textDecoration: "underline" }}
                                onClick={() => {
                                    const recordToStore = {
                                        ...record,
                                        vehicle_lcr_id: record.vehicleLcrNum
                                    };
                                    sessionStorage.setItem("LCR_REPORT_RECORD", JSON.stringify(recordToStore));
                                    window.open(window.location.origin + "/lcr-report", "_blank");
                                }}
                            >
                                {text}
                            </span>
                        )
                    },
                    {
                        title: "Stop LCR Number",
                        dataIndex: "stopLcrNum",
                        key: "stopLcrNum",
                        width: 20,
                        sorter: true,
                        render: (text, record) => (
                            <span
                                style={{ color: "#2977ff", cursor: "pointer", textDecoration: "underline" }}
                                onClick={() => {
                                    const recordToStore = {
                                        ...record,
                                        vehicle_lcr_id: record.stopLcrNum,
                                        travel_by: record.mode
                                    };
                                    sessionStorage.setItem("LCR_REPORT_RECORD", JSON.stringify(recordToStore));
                                    window.open(window.location.origin + "/lcr-report", "_blank");
                                }}
                            >
                                {text}
                            </span>
                        )
                    },
                    { title: "LCR Departed Date & Time", dataIndex: "tripDepartedTime", key: "tripDepartedTime", sorter: true, align: "center", },
                    { title: "To Hub", dataIndex: "toHub", key: "toHub", sorter: true },
                    { title: "Mode", dataIndex: "mode", key: "mode", sorter: true },
                    { title: "Vehicle Number", dataIndex: "vehicleNum", key: "vehicleNum", sorter: true, align: "center", },
                    { title: "Flight Number", dataIndex: "flightNum", key: "flightNum", sorter: true, align: "center", },
                    {
                        title: "Bags Out | In",
                        dataIndex: "bagOutCount",
                        key: "bagOutCount",
                        align: "center",
                        sorter: true,
                        width: 20,
                        render: (text, record) => (
                            <>
                                {record.bagOutCount == 0 ? <span>{record.bagOutCount ?? ""}</span> : (
                                    <span
                                        style={{ color: "#2977ff", cursor: "pointer", textDecoration: "underline" }}
                                        onClick={() => handleCellClick("bagOut", record)}
                                    >
                                        {record.bagOutCount ?? ""}
                                    </span>)}
                                <span style={{ fontWeight: "bold", padding: "0 4px" }}>|</span>
                                {record.bagInCount == 0 ? <span>{record.bagInCount ?? ""}</span> : (
                                    <span
                                        style={{ color: "#2977ff", cursor: "pointer", textDecoration: "underline" }}
                                        onClick={() => handleCellClick("bagIn", record)}
                                    >
                                        {record.bagInCount ?? ""}
                                    </span>)}
                            </>
                        )
                    },

                    {
                        title: "Bags Pending",
                        dataIndex: "bagPendingCount",
                        key: "bagPendingCount",
                        sorter: true,
                        width: 20,
                        align: "center",
                        render: (text, record) => (
                            <>
                                {record.bagPendingCount == 0 ? <span>{record.bagPendingCount ?? ""}</span> : (
                                    <span
                                        style={{ color: "#2977ff", cursor: "pointer", textDecoration: "underline" }}
                                        onClick={() => handleCellClick("bagPending", record)}
                                    >
                                        {record.bagPendingCount ?? ""}
                                    </span>)}
                            </>
                        )
                    },
                    {
                        title: "In Bag Shipments Out | In",
                        dataIndex: "inBagShipmentOutCount",
                        key: "inBagShipmentOutCount",
                        align: "center",
                        sorter: true,
                        width: 20,
                        render: (text, record) => (
                            <>
                                {record.inBagShipmentOutCount == 0 ? <span>{record.inBagShipmentOutCount ?? ""}</span> : (
                                    <span
                                        style={{ color: "#2977ff", cursor: "pointer", textDecoration: "underline" }}
                                        onClick={() => handleCellClick("inBagShipmentOut", record)}
                                    >
                                        {record.inBagShipmentOutCount ?? ""}
                                    </span>)}
                                <span style={{ fontWeight: "bold", padding: "0 4px" }}>|</span>
                                {record.inBagShipmentInCount == 0 ? <span>{record.inBagShipmentInCount ?? ""}</span> : (
                                    <span
                                        style={{ color: "#2977ff", cursor: "pointer", textDecoration: "underline" }}
                                        onClick={() => handleCellClick("inBagShipmentIn", record)}
                                    >
                                        {record.inBagShipmentInCount ?? ""}
                                    </span>)}
                            </>
                        )
                    },
                    {
                        title: "In Bag Shipments Pending",
                        dataIndex: "inBagShipmentVariance",
                        key: "inBagShipmentVariance",
                        sorter: true,
                        align: "center",
                        width: 20,
                        render: (text, record) => (
                            <>
                                {record.inBagShipmentVariance == 0 ? <span>{record.inBagShipmentVariance ?? ""}</span> : (
                                    <span
                                        style={{ color: "#2977ff", cursor: "pointer", textDecoration: "underline" }}
                                        onClick={() => handleCellClick("inBagShipmentVariance", record)}
                                    >
                                        {record.inBagShipmentVariance ?? ""}
                                    </span>)}
                            </>
                        )
                    },
                    {
                        title: "Individual Shipments Out | In",
                        dataIndex: "shipmentOutCount",
                        key: "shipmentOutCount",
                        align: "center",
                        sorter: true,
                        width: 20,
                        render: (text, record) => (
                            <>
                                {record.shipmentOutCount == 0 ? <span>{record.shipmentOutCount ?? ""}</span> : (
                                    <span
                                        style={{ color: "#2977ff", cursor: "pointer", textDecoration: "underline" }}
                                        onClick={() => handleCellClick("shipmentOut", record)}
                                    >
                                        {record.shipmentOutCount ?? ""}
                                    </span>)}
                                <span style={{ fontWeight: "bold", padding: "0 4px" }}>|</span>
                                {record.shipmentInCount == 0 ? <span>{record.shipmentInCount ?? ""}</span> : (
                                    <span
                                        style={{ color: "#2977ff", cursor: "pointer", textDecoration: "underline" }}
                                        onClick={() => handleCellClick("shipmentIn", record)}
                                    >
                                        {record.shipmentInCount ?? ""}
                                    </span>)}
                            </>
                        )
                    },
                    {
                        title: "Individual Shipments Pending",
                        dataIndex: "shipmentPendingCount",
                        key: "shipmentPendingCount",
                        sorter: true,
                        align: "center",
                        width: 20,
                        render: (text, record) => (
                            <>
                                {record.shipmentPendingCount == 0 ? <span>{record.shipmentPendingCount ?? ""}</span> : (
                                    <span
                                        style={{ color: "#2977ff", cursor: "pointer", textDecoration: "underline" }}
                                        onClick={() => handleCellClick("shipmentPending", record)}
                                    >
                                        {record.shipmentPendingCount ?? ""}
                                    </span>)}
                            </>
                        )
                    },

                    { title: "Co Loader", dataIndex: "coLoader", key: "coLoader", sorter: true, align: "center", },
                    { title: "Driver Name", dataIndex: "driverName", key: "driverName", sorter: true, align: "center", },
                    { title: "Driver Phone No", dataIndex: "driverPhoneNo", key: "driverPhoneNo", sorter: true, align: "center", },
                    { title: "Total Weight (kg)", dataIndex: "totalWeight", key: "totalWeight", sorter: true, align: "center", },
                    { title: "Status", dataIndex: "status", key: "status", sorter: true, align: "center", },
                    { title: "Is Anomaly", dataIndex: "isAnomaly", key: "isAnomaly", sorter: true, align: "center", },

                ]}
                dataSource={reportData}
                loading={loading}
                pagination={pagination}
                onChange={handleTableChange}
                rowKey="lcrNum"
                style={{ marginTop: 32 }}
            />
        </div>
    );
};

export default OutgoingLCRReport;
